import { useCallback, useEffect, useMemo, useRef } from 'react'

export type UseLongPressOptions = {
  ms?: number
  driftPx?: number
  onLongPress: (event: PointerEvent) => void
  disabled?: boolean
}

export const useLongPress = ({ ms = 450, driftPx = 4, onLongPress, disabled = false }: UseLongPressOptions) => {
  const timerRef = useRef<number | null>(null)
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const eventRef = useRef<PointerEvent | null>(null)
  const scrollCleanupRef = useRef<(() => void) | null>(null)
  const selectionStyleRef = useRef<{ element: HTMLElement; userSelect: string; webkitUserSelect: string; webkitTouchCallout: string } | null>(null)

  const restoreSelectionStyle = useCallback(() => {
    const previous = selectionStyleRef.current
    if (!previous) return
    const restore = (property: string, value: string) => {
      if (value) previous.element.style.setProperty(property, value)
      else previous.element.style.removeProperty(property)
    }
    restore('user-select', previous.userSelect)
    restore('-webkit-user-select', previous.webkitUserSelect)
    restore('-webkit-touch-callout', previous.webkitTouchCallout)
    selectionStyleRef.current = null
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current !== null && typeof window !== 'undefined') window.clearTimeout(timerRef.current)
    timerRef.current = null
    startRef.current = null
    eventRef.current = null
    scrollCleanupRef.current?.()
    scrollCleanupRef.current = null
    restoreSelectionStyle()
  }, [restoreSelectionStyle])

  useEffect(() => cancel, [cancel])

  const onPointerDown = useCallback((event: React.PointerEvent | PointerEvent) => {
    if (disabled || typeof window === 'undefined' || typeof document === 'undefined') return
    cancel()
    const target = event.currentTarget
    if (target instanceof HTMLElement) {
      selectionStyleRef.current = {
        element: target,
        userSelect: target.style.getPropertyValue('user-select'),
        webkitUserSelect: target.style.getPropertyValue('-webkit-user-select'),
        webkitTouchCallout: target.style.getPropertyValue('-webkit-touch-callout'),
      }
      target.style.setProperty('user-select', 'none')
      target.style.setProperty('-webkit-user-select', 'none')
      target.style.setProperty('-webkit-touch-callout', 'none')
    }
    startRef.current = { x: event.clientX, y: event.clientY }
    eventRef.current = 'nativeEvent' in event ? event.nativeEvent : event
    const onScroll = () => cancel()
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, once: true })
    scrollCleanupRef.current = () => document.removeEventListener('scroll', onScroll, true)
    timerRef.current = window.setTimeout(() => {
      const nativeEvent = eventRef.current
      cancel()
      if (nativeEvent) onLongPress(nativeEvent)
    }, ms)
  }, [cancel, disabled, ms, onLongPress])

  const onPointerMove = useCallback((event: React.PointerEvent | PointerEvent) => {
    const start = startRef.current
    if (!start) return
    if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > driftPx) cancel()
  }, [cancel, driftPx])

  return useMemo(
    () => ({ onPointerDown, onPointerUp: cancel, onPointerMove, onPointerCancel: cancel }),
    [onPointerDown, cancel, onPointerMove],
  )
}
